import { FolderIcon } from '@assets/icons';
import { GridBox } from '@components/grid-box';
import { Image } from '@components/image';
import { OptionsMenu } from '@components/menu';
import { useConfirmation, useModal } from '@components/modal';
import {
  GetVolumeDataQuery,
  GetVolumeDetailsVolumeDataQuery,
  useDeleteVolumeDataMutation,
} from '@generated/graphql';
import { saveFile } from '@helper-functions/save-file';
import { Stack, useTheme } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { isNil } from 'lodash';
import moment from 'moment';
import { Dispatch, SetStateAction } from 'react';
import { Preview3DModal } from '../Preview3DModal';
import { RenameItemModal } from '../RenameItemModal';
import { ThumbnailUploadModal } from '../ThumbnailUploadModal';

const supportedImageExtensions = ['png', 'gif', 'jpeg', 'jpg'];

type GridViewItemProps = {
  volume: GetVolumeDataQuery['getVolumeData']['keys'][0] & { type?: string };
  volumeMetadata: GetVolumeDetailsVolumeDataQuery['getVolumes'][0];
  volumeId: string;
  setDirectory: Dispatch<SetStateAction<string[]>>;
  directory: string[];
  refetchVolumes: () => Promise<unknown>;
};

export const GridViewItem = ({
  volume,
  volumeMetadata,
  volumeId,
  setDirectory,
  directory,
  refetchVolumes,
}: GridViewItemProps) => {
  const { palette } = useTheme();
  const { showConfirmation } = useConfirmation();
  const { useAsyncNotification, addNotification } = useNotifications();
  const [deleteVolumeData] = useDeleteVolumeDataMutation();
  const handleDownload = () => {
    saveFile({
      url: volume?.url,
      name: `${volume?.key}`,
    });
    addNotification({
      text: 'Starting download...',
      type: 'success',
      duration: 6000,
    });
  };
  const getDropdownItems = () => {
    if (volumeMetadata.permission === 'view' || volumeMetadata.permission === 'read') {
      if (volume?.type !== 'folder') {
        return [{ label: 'Download', onClick: handleDownload }];
      }
      return null;
    }

    const items: { label: string; onClick: () => void }[] = [];
    if (volume?.type !== 'folder') {
      items.push({ label: 'Download', onClick: handleDownload });
    }
    if (volumeMetadata.permission === 'write') {
      items.push({ label: 'Rename', onClick: handleShowRenameModal });
      items.push({ label: 'Thumbnail', onClick: handleShowSetThumbnailModal });
      items.push({ label: 'Delete', onClick: handleShowDeleteModal });
    }
    return items;
  };

  const { showModal: showRenameModal } = useModal({
    component: RenameItemModal,
    componentProps: { name: volume.key, volumeId, refetchVolumes, directory },
  });
  const { showModal: showVolumeThumbnailUploadModal } = useModal({
    component: ThumbnailUploadModal,
    componentProps: {
      name: volume.key,
      volumeId,
      directory,
      refetchVolumeData: refetchVolumes,
    },
    modalProps: { title: 'Upload thumbnail' },
  });

  const handleShowRenameModal = () => {
    showRenameModal({
      name: volume?.key,
    });
  };
  const handleShowSetThumbnailModal = () => {
    showVolumeThumbnailUploadModal({
      name: volume?.key,
    });
  };
  const handleShowDeleteModal = async () => {
    await showConfirmation({
      onAffirm: async () => {
        await useAsyncNotification('Successfully deleted item.', async () => {
          await deleteVolumeData({
            variables: { volumeId, keys: [[...directory, volume?.key].join('')] },
          });
          await refetchVolumes();
        })();
      },
      message: `Are you sure you want to delete ${[...directory, volume?.key].join('')}?`,
    });
  };

  const { showModal: showVolume3DPreviewModal } = useModal({
    component: Preview3DModal,
    componentProps: {
      name: volume.key,
      previewUrl: volume.previewUrl,
      imageUrl: volume.url,
      options:
        volumeMetadata.permission === 'view' || volumeMetadata.permission === 'read' ? null : (
          <OptionsMenu items={getDropdownItems()} keepMounted={false} />
        ),
    },
    modalProps: { disableAutoFocus: true, disablePortal: true },
  });

  const getDescription = () => {
    if (volume?.type === 'folder') {
      return 'Folder';
    }
    if (!isNil(volume?.updatedAt)) {
      return `Edited ${moment(volume?.updatedAt).fromNow()}`;
    }
    return '-';
  };

  const handleGridBoxClick =
    volume?.type === 'folder'
      ? () => setDirectory((fh) => [...fh, volume?.key])
      : volume?.previewUrl
        ? showVolume3DPreviewModal
        : supportedImageExtensions.includes(volume.key.split('.').pop())
          ? showVolume3DPreviewModal
          : undefined;

  return (
    <GridBox
      sx={{ height: '100%', width: '100%' }}
      title={volume?.key}
      description={getDescription()}
      onClick={handleGridBoxClick}
      content={
        volume?.type === 'folder' ? (
          <Stack
            justifyContent="center"
            alignItems="center"
            height="100%"
            sx={{ backgroundColor: palette.background.default }}
          >
            <FolderIcon size="40%" />
          </Stack>
        ) : (
          <Image url={volume?.thumbnailUrl} />
        )
      }
      menuItems={getDropdownItems()}
    />
  );
};
